/**
 * Generates a username from a first name and last name.
 *
 * Accented characters are transliterated to their base Latin equivalents
 * (e.g. "é" → "e", "ñ" → "n") rather than being stripped entirely.
 */
export function generateUsername(firstName: string, lastName: string): string {
  return `${firstName}${lastName}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z]/g, "");
}

/**
 * Reserved usernames that a coach profile (served at the top-level `/$username`
 * route) must never be allowed to take, because they would shadow — or be
 * shadowed by — a real system route living in the same namespace.
 *
 * Sources:
 *  - top-level route segments under `src/routes/*` and the pathless
 *    `_public`/`_user`/`_auth` layouts, whose children are also served at the
 *    top level (a unit test in `__tests__/username.source.test.ts` enumerates the
 *    route dirs and fails when a new top-level segment is not reserved),
 *  - marketing / Builder.io pages referenced by the product
 *    (`/about`, `/pricing`, `/partner`, `/nova`, …),
 *  - a defensive set of conventional web/system words.
 *
 * Entries are stored lowercased. Multi-word route segments are kept in their
 * hyphenated form (e.g. `coaching-for`) so a hand-picked username can't shadow
 * them either; auto-generated usernames are `[a-z]+` only and so can only ever
 * collide with the single-word entries.
 */
export const RESERVED_USERNAMES: ReadonlySet<string> = new Set([
  // ── top-level app routes (src/routes/*) ──────────────────────────────────
  "404",
  "accept-invitation",
  "admin",
  "api",
  "blog",
  "coach",
  "embed",
  "intake",
  "lc-payment",
  "summit",
  "summit-special",
  "well-known",
  // ── public routes (src/routes/_public/*) ─────────────────────────────────
  "articles",
  "brand",
  "6-figure-readiness-score",
  // Retired public path, still redirected at the edge.
  "business-health-score",
  "ccos",
  "coach-business-builder",
  "coaches",
  "coaching-business",
  "coaching-for",
  "coaching-intelligence",
  "dcm-vs-betterup",
  "discovery",
  "frameworks",
  "goals",
  "how-it-works",
  "organization-invite",
  "organizations",
  "our-coaches",
  "page",
  "privacy-policy",
  "stories",
  "training-programs",
  "vision",
  "w",
  "what-we-offer",
  "wheel",
  "wheel-of-life",
  // ── user-side routes (src/routes/_user/*) ────────────────────────────────
  "become-a-coach",
  "coach-discovery",
  "coach-finder",
  "coaching",
  "contacts",
  "dashboard",
  "life-design",
  "messages",
  "my-coaching",
  "my-organization",
  "pro-plus",
  "referrals",
  "settings",
  "summits",
  "welcome",
  // ── auth routes (src/routes/_auth/*) ─────────────────────────────────────
  "accept-token",
  "forgot-password",
  "login",
  "signin",
  "signup",
  // ── sitemaps / static / metadata ─────────────────────────────────────────
  "sitemap",
  "robots",
  "favicon",
  "manifest",
  "assets",
  "static",
  "public",
  // ── marketing / Builder.io pages referenced by the product ───────────────
  "about",
  "pricing",
  "partner",
  "partners",
  "nova",
  // ── auth & account surfaces (defensive) ──────────────────────────────────
  "auth",
  "logout",
  "sign-in",
  "sign-up",
  "register",
  "account",
  "accounts",
  "billing",
  "onboarding",
  // ── conventional reserved web/system words (defensive) ───────────────────
  "app",
  "home",
  "index",
  "root",
  "me",
  "us",
  "you",
  "user",
  "users",
  "profile",
  "profiles",
  "search",
  "explore",
  "discover",
  "help",
  "support",
  "contact",
  "terms",
  "privacy",
  "legal",
  "security",
  "status",
  "docs",
  "faq",
  "careers",
  "press",
  "news",
  "article",
  "pages",
  "null",
  "undefined",
  "madhuchandarsekaran", // Historical public URL redirects to madhuc.
  "constructor", // Public redirect lookup must not resolve Object.prototype.
  "true",
  "false",
]);

/**
 * Whether `username` collides with a reserved system route/word. Comparison is
 * case-insensitive and trims surrounding whitespace; the candidate is otherwise
 * compared verbatim (no hyphen-stripping) so hand-picked usernames can't shadow
 * hyphenated routes such as `coaching-for`.
 */
export function isReservedUsername(username: string): boolean {
  return RESERVED_USERNAMES.has(username.trim().toLowerCase());
}

/** Append a short random suffix used to disambiguate a username. */
function withRandomSuffix(base: string): string {
  return `${base}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Resolve a username that is neither reserved nor already taken, starting from
 * `base`. Reserved or taken candidates get a short random suffix appended and
 * are re-checked. `isTaken` is injected by the caller (e.g. a `getCoachByUsername`
 * lookup) so this helper stays pure and unit-testable. An empty `base`
 * (possible when a name transliterates to nothing) falls back to `"coach"`.
 */
export async function ensureUsernameAvailable(
  base: string,
  isTaken: (candidate: string) => boolean | Promise<boolean>,
): Promise<string> {
  const seed = base || "coach";
  let candidate = seed;
  for (let attempt = 0; attempt < 10; attempt++) {
    if (!isReservedUsername(candidate) && !(await isTaken(candidate))) {
      return candidate;
    }
    candidate = withRandomSuffix(seed);
  }
  // Extremely unlikely fallback: time-seed to all-but-guarantee uniqueness.
  return withRandomSuffix(`${seed}${Date.now().toString(36)}`);
}

/**
 * The canonical username an authored profile slug most likely meant, when that
 * differs from the slug itself — otherwise `null`.
 *
 * Auto-generated usernames are separator-free ASCII: `generateUsername`
 * lowercases, transliterates accents (NFD, drop combining marks) and discards
 * everything else. Content authored elsewhere routinely writes the display
 * form instead — `/first-last`, or `/auśra-masevičiūtė` for a coach stored as
 * `ausramaseviciute` — and those URLs 404 even though the profile exists.
 * Applying the same folding recovers the stored username from either spelling.
 *
 * Digits are kept (unlike `generateUsername`, which only ever emits `[a-z]`)
 * because a hand-picked username may contain them, and dropping them here
 * would aim the redirect at the wrong coach.
 *
 * This is only ever a *candidate*: hand-picked usernames MAY legitimately
 * contain hyphens (the admin schema allows `a-b-c`), so the caller must prefer
 * the verbatim username and confirm a coach actually owns the candidate before
 * redirecting. Returns `null` when folding changes nothing (no second lookup
 * worth doing) or leaves nothing usable, so junk paths can't become redirects.
 */
export function canonicalUsernameCandidate(username: string): string | null {
  const normalized = username.trim().toLowerCase();
  const candidate = normalized
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .replace(/[^a-z0-9]/gu, "");
  if (!candidate || candidate === normalized) return null;
  return candidate;
}

/** `href` with its first path segment (the old username) swapped for the new one. */
export function renamedCoachPath(
  href: string,
  oldUsername: string,
  newUsername: string,
): string {
  const prefix = `/${oldUsername}`;
  const rest = href.startsWith(prefix) ? href.slice(prefix.length) : "";
  return `/${newUsername}${rest}`;
}
